/**
 * useCollaboration — sets up Yjs + Socket.IO real-time sync.
 *
 * Provides a CollabDelegate that all section mutations must go through when
 * collabActive === true, ensuring every change is reflected in the shared
 * Yjs document before updating local Zustand state.
 */
import { useEffect, useRef, useCallback } from "react";
import * as Y from "yjs";
import { io } from "socket.io-client";
import { useEditorStore } from "../stores/editorStore";
import { API_URL } from "../../../api/api";

const COLLAB_URL = (API_URL || "").replace(/^http/, "http");

export function useCollaboration(newsletterId) {
  const {
    setCollabActive,
    setCollabUsers,
    setCollabDelegate,
    hydrateSections,
  } = useEditorStore();

  const socketRef = useRef(null);
  const ydocRef = useRef(null);

  // ── CollabDelegate ────────────────────────────────────────────────────────
  // Wraps a mutation: applies it to Yjs first, then Yjs observer updates Zustand.
  const collabDelegate = useCallback((mutation) => {
    if (!ydocRef.current) return;
    // We apply the mutation to Zustand directly here; Yjs observer will sync
    // the change to remote peers.
    const fakeSet = (updater) => {
      const store = useEditorStore.getState();
      const partial = typeof updater === "function" ? updater(store) : updater;
      if (!partial.sections) return;
      // Sync new sections into the Yjs array
      const ySections = ydocRef.current.getArray("sections");
      ydocRef.current.transact(() => {
        ySections.delete(0, ySections.length);
        ySections.insert(
          0,
          partial.sections.map((s) => JSON.stringify(s)),
        );
      });
      // Also update local store
      useEditorStore.setState(partial);
    };
    mutation(fakeSet, useEditorStore.getState);
  }, []);

  useEffect(() => {
    if (!newsletterId) return;

    const ydoc = new Y.Doc();
    ydocRef.current = ydoc;

    const userData = (() => {
      try {
        return JSON.parse(localStorage.getItem("userdata") || "{}");
      } catch {
        return {};
      }
    })();
    const user = {
      id: userData.id,
      username: userData.username || "User",
      color: randomColor(),
    };

    // Socket.IO connection
    const socket = io(`${COLLAB_URL}/newsletter-collab`, {
      auth: { token: localStorage.getItem("token") },
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setCollabActive(true);
      setCollabDelegate(collabDelegate);
      socket.emit("join_room", { newsletterId, user });
    });

    // Receive initial state
    socket.on("room_state", (encodedState) => {
      Y.applyUpdate(ydoc, new Uint8Array(encodedState));
      syncYjsToStore(ydoc, hydrateSections);
    });

    // Receive incremental updates from peers
    socket.on("yjs_update", (update) => {
      Y.applyUpdate(ydoc, new Uint8Array(update));
      syncYjsToStore(ydoc, hydrateSections);
    });

    // Presence
    socket.on("presence_update", (users) => {
      setCollabUsers(users.filter((u) => u.socketId !== socket.id));
    });

    socket.on("disconnect", () => setCollabActive(false));
    socket.on("connect_error", (err) => {
      console.warn("[collab] connect error:", err.message);
      setCollabActive(false);
    });

    // Broadcast local Yjs changes to server
    ydoc.on("update", (update) => {
      socket.emit("yjs_update", { newsletterId, update: Array.from(update) });
    });

    return () => {
      socket.emit("leave_room", { newsletterId });
      socket.disconnect();
      ydoc.destroy();
      setCollabActive(false);
      setCollabUsers([]);
      setCollabDelegate(null);
    };
  }, [newsletterId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ydoc: ydocRef.current, socket: socketRef.current };
}

function syncYjsToStore(ydoc, hydrateSections) {
  const ySections = ydoc.getArray("sections");
  const sections = ySections.toArray().map((s) => {
    try {
      return typeof s === "string" ? JSON.parse(s) : s;
    } catch {
      return s;
    }
  });
  hydrateSections(sections);
}

function randomColor() {
  const colors = [
    "#f87171",
    "#fb923c",
    "#fbbf24",
    "#34d399",
    "#38bdf8",
    "#818cf8",
    "#e879f9",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}
